'use client';

import { useEffect, useRef, useState } from 'react';

type Props = {
  mp4Src: string;
  webmSrc?: string;
  poster?: string;
  className?: string;
};

export default function VideoBackground({ mp4Src, webmSrc, poster, className }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [canLoad, setCanLoad] = useState(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCanLoad(true);
            obs.disconnect();
          }
        });
      },
      { rootMargin: '200px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!canLoad || !videoRef.current) return;
    const v = videoRef.current;
    v.play().catch(() => {/* ignore */ });
  }, [canLoad]);

  return (
    <video
      ref={videoRef}
      className={className}
      muted
      loop
      playsInline
      autoPlay
      preload="metadata"
      poster={poster}
    >
      {canLoad && webmSrc && <source src={webmSrc} type="video/webm" />}
      {canLoad && <source src={mp4Src} type="video/mp4" />}
      Your browser does not support the video tag.
    </video>
  );
}
